"use client";

import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LOCALE_COOKIE, locales, type Locale } from "@/lib/i18n";

// ── Shared locale-switch logic ─────────────────────────────────────────────

function getLocalizedPath(pathname: string, locale: Locale) {
  const segments = pathname.split("/");

  if (segments[1] && locales.includes(segments[1] as Locale)) {
    segments[1] = locale;
  } else {
    segments.splice(1, 0, locale);
  }

  return segments.join("/") || `/${locale}`;
}

export function useSwitchLocale(currentLocale: Locale) {
  const router = useRouter();
  const pathname = usePathname();

  return function switchLocale(newLocale: Locale) {
    if (newLocale === currentLocale) return;

    document.cookie = `${LOCALE_COOKIE}=${newLocale};path=/;max-age=${60 * 60 * 24 * 365}`;

    const search = typeof window !== "undefined" ? window.location.search : "";
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const nextPath = getLocalizedPath(pathname, newLocale);

    router.push(`${nextPath}${search}${hash}`);
  };
}

// ── The language pill: [বাং | EN] ────────────────────────────────────────

/**
 * The language switch on every bar and phone menu: the site bar from `lg`, the
 * site's phone menu, the Judge bar and its menu. Each language is a `Button`
 * (`segment` / `segmentDark`, `chip` / `chipTap` / `chipLg`, `docs/03` §6), the
 * one in use `aria-pressed`. The site bar's desktop dropdown that lived in this
 * file had no caller after `SITE-NAV01` and was removed on 2026-10-02.
 */
export function LocalePill({
  locale: currentLocale,
  visibleLocales,
  tone = "light",
  size = "sm",
}: {
  locale: Locale;
  visibleLocales?: Locale[];
  /** `dark` sits on the Judge shell's dark bar (`EX-PUB19`); every other caller is on a light surface. */
  tone?: "light" | "dark";
  /**
   * `md` is a phone menu's language row (`SITE-NAV01`, the Judge bar's menu): a 44 px thumb target, not a chip in a bar.
   * `bar` is `sm`'s chip on a bar a tablet reaches (the Judge bar from `sm`): each language 44 px wide, taking
   * taps over 44 px tall through a transparent `::before`, so the bar keeps its height.
   */
  size?: "sm" | "md" | "bar";
}) {
  const options =
    visibleLocales && visibleLocales.length > 0 ? visibleLocales : [...locales];
  const switchLocale = useSwitchLocale(currentLocale);

  return (
    <div
      className={cn(
        "flex items-center rounded-full p-0.5",
        tone === "dark" ? "border border-white/10 bg-white/[0.04]" : "bg-neutral-100"
      )}
      role="group"
      aria-label="Switch language"
    >
      {options.map((loc) => {
        const isActive = loc === currentLocale;
        const label = loc === "en" ? "EN" : "বাং";

        return (
          <Button
            key={loc}
            type="button"
            variant={tone === "dark" ? "segmentDark" : "segment"}
            size={size === "md" ? "chipLg" : size === "bar" ? "chipTap" : "chip"}
            onClick={() => switchLocale(loc)}
            aria-pressed={isActive}
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
}
